import { MonoTypeOperatorFunction } from "rxjs";
import { tap } from "rxjs/operators";

/**
 * Logging in the application.
 * {@link LoggerFactoryService} on how to create an instance while using DI.
 */
export class Logger {
  public static logDebug = true; // isDevMode()

  private errLoggers: ((
    source: string,
    msg: string,
    ...params: unknown[]
  ) => void)[];
  private infoLoggers: ((
    source: string,
    msg: string,
    ...params: unknown[]
  ) => void)[];
  private warnLoggers: ((
    source: string,
    msg: string,
    ...params: unknown[]
  ) => void)[];

  constructor(private source: string) {
    this.infoLoggers = [];
    this.warnLoggers = [];
    this.errLoggers = [];
    if (window.console && window.console.log) {
      this.infoLoggers.push((s, m, ...p) => {
        window.console.log(this.formatOutput(s, m), ...p);
      });

      // warnings
      let warnLog = (s: string, m: string, ...p: unknown[]) => {
        window.console.log(this.formatOutput(s, "WARNING: " + m), ...p);
      };
      if (window.console.warn) {
        warnLog = (s, m, ...p) => {
          window.console.warn(this.formatOutput(s, m), ...p);
        };
      }
      this.warnLoggers.push(warnLog);

      // errors
      let errLog = (s: string, m: string, ...p: unknown[]) => {
        window.console.log(this.formatOutput(s, "ERROR: " + m), ...p);
      };
      if (window.console.error) {
        errLog = (s, m, ...p) => {
          window.console.error(this.formatOutput(s, m), ...p);
        };
      }
      this.errLoggers.push(errLog);
    }
  }

  /**
   * Log debug.
   *
   * Logs to info-output, currently but only if {@link LoggerFactoryService#logDebug} is true.
   *
   * @param msg - the message to log.
   * @param params - optional params to log
   */
  public debug(msg: string, ...params: unknown[]): void {
    if (!Logger.logDebug) {
      return;
    }

    for (const l of this.infoLoggers) {
      l(this.source, msg, ...params);
    }
  }
  /**
   * Log errors.
   *
   * @param msg - the message to log.
   * @param params - optional params to log
   */
  public error(msg: string, ...params: unknown[]): void {
    for (const l of this.errLoggers) {
      l(this.source, msg, ...params);
    }
  }

  /**
   * Log informations.
   *
   * @param msg - the message to log.
   * @param params - optional params to log
   */
  public info(msg: string, ...params: unknown[]): void {
    for (const l of this.infoLoggers) {
      l(this.source, msg, ...params);
    }
  }

  /**
   * Log warnings.
   *
   * @param msg - the message to log.
   * @param params - optional params to log
   */
  public warn(msg: string, ...params: unknown[]): void {
    for (const l of this.warnLoggers) {
      l(this.source, msg, ...params);
    }
  }

  /**
   * an Rx-operator to debug an observable.
   * use it like `getObservable().pipe(this.logger.observableOperator("getObservable"))`
   * to log next, error and completes of the observable.
   * @param name name of the observable in this logger.
   */
  public observableOperator<T>(
    name: string,
    level?: "debug" | "info" | "warn"
  ): MonoTypeOperatorFunction<T> {
    if (!level) {
      level = "debug";
    }
    return tap<T>(
      (next: T) => {
        this[level]("Rx:" + name + " NEXT", next);
      },
      (err: unknown) => {
        this.error("Rx:" + name + " ERROR", err);
      },
      () => {
        this[level]("Rx:" + name + " COMPLETE");
      }
    );
  }

  private formatOutput(source: string, msg: string) {
    return "[" + source + "] " + msg;
  }
}
